/**
 * GLOBAL-FILTER-ENGINE-000001 — URL ↔ Universal context codec.
 * Short `gfe_*` params; unknown keys left untouched for page-local query.
 */
import { DEFAULT_CONTEXT } from './defaults';
import type {
  ComparePeriod,
  DateRangePreset,
  TimeGrain,
  UniversalBusinessContext,
} from './types';

const DATE: DateRangePreset[] = [
  'today',
  'yesterday',
  '7d',
  '30d',
  'mtd',
  'qtd',
  'ytd',
  'custom',
];
const COMPARE: ComparePeriod[] = ['none', 'previous_period', 'previous_year', 'custom'];
const GRAIN: TimeGrain[] = ['hour', 'day', 'week', 'month', 'quarter', 'year'];

/** Keys owned by the global filter engine in the URL. */
export const GFE_URL_KEYS = [
  'gfe_seller',
  'gfe_seller_label',
  'gfe_conn',
  'gfe_mp',
  'gfe_date',
  'gfe_cmp',
  'gfe_bu',
  'gfe_wh',
  'gfe_state',
  'gfe_city',
  'gfe_cat',
  'gfe_subcat',
  'gfe_brand',
  'gfe_asin',
  'gfe_sku',
  'gfe_fnsku',
  'gfe_ptype',
  'gfe_ostatus',
  'gfe_istatus',
  'gfe_pstatus',
  'gfe_rstatus',
  'gfe_hour',
  'gfe_day',
  'gfe_week',
  'gfe_month',
  'gfe_quarter',
  'gfe_year',
  'gfe_grain',
  'gfe_q',
  'gfe_ws',
] as const;

function emptyToNull(s: string | null): string | null {
  if (s == null || s === '') return null;
  return s;
}

function parseDate(v: string | null): DateRangePreset {
  if (v && (DATE as string[]).includes(v)) return v as DateRangePreset;
  return DEFAULT_CONTEXT.dateRange;
}

function parseCompare(v: string | null): ComparePeriod {
  if (v && (COMPARE as string[]).includes(v)) return v as ComparePeriod;
  return DEFAULT_CONTEXT.comparePeriod;
}

function parseGrain(v: string | null): TimeGrain {
  if (v && (GRAIN as string[]).includes(v)) return v as TimeGrain;
  return null;
}

function parseHour(v: string | null): number | null {
  if (v == null || v === '') return null;
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0 || n > 23) return null;
  return Math.floor(n);
}

/** Decode GFE params from URLSearchParams (or full search string). */
export function contextFromSearchParams(
  sp: URLSearchParams,
  base: UniversalBusinessContext = DEFAULT_CONTEXT,
): UniversalBusinessContext {
  const g = (k: string) => sp.get(k);
  const hasAny = GFE_URL_KEYS.some((k) => sp.has(k));
  if (!hasAny) return base;

  return {
    sellerId: emptyToNull(g('gfe_seller')) ?? base.sellerId,
    sellerLabel: g('gfe_seller_label') || base.sellerLabel,
    connectionId: emptyToNull(g('gfe_conn')) ?? base.connectionId,
    marketplace: g('gfe_mp') || base.marketplace,
    dateRange: g('gfe_date') != null ? parseDate(g('gfe_date')) : base.dateRange,
    comparePeriod: g('gfe_cmp') != null ? parseCompare(g('gfe_cmp')) : base.comparePeriod,
    businessUnit: emptyToNull(g('gfe_bu')) ?? base.businessUnit,
    warehouseId: emptyToNull(g('gfe_wh')) ?? base.warehouseId,
    state: emptyToNull(g('gfe_state')) ?? base.state,
    city: emptyToNull(g('gfe_city')) ?? base.city,
    category: emptyToNull(g('gfe_cat')) ?? base.category,
    subCategory: emptyToNull(g('gfe_subcat')) ?? base.subCategory,
    brand: emptyToNull(g('gfe_brand')) ?? base.brand,
    asin: emptyToNull(g('gfe_asin')) ?? base.asin,
    sku: emptyToNull(g('gfe_sku')) ?? base.sku,
    fnsku: emptyToNull(g('gfe_fnsku')) ?? base.fnsku,
    productType: emptyToNull(g('gfe_ptype')) ?? base.productType,
    orderStatus: emptyToNull(g('gfe_ostatus')) ?? base.orderStatus,
    inventoryStatus: emptyToNull(g('gfe_istatus')) ?? base.inventoryStatus,
    pricingStatus: emptyToNull(g('gfe_pstatus')) ?? base.pricingStatus,
    returnStatus: emptyToNull(g('gfe_rstatus')) ?? base.returnStatus,
    hour: g('gfe_hour') != null ? parseHour(g('gfe_hour')) : base.hour,
    day: emptyToNull(g('gfe_day')) ?? base.day,
    week: emptyToNull(g('gfe_week')) ?? base.week,
    month: emptyToNull(g('gfe_month')) ?? base.month,
    quarter: emptyToNull(g('gfe_quarter')) ?? base.quarter,
    year: emptyToNull(g('gfe_year')) ?? base.year,
    timeGrain: g('gfe_grain') != null ? parseGrain(g('gfe_grain')) : base.timeGrain,
    search: g('gfe_q') ?? base.search,
    lastWorkspacePath: emptyToNull(g('gfe_ws')) ?? base.lastWorkspacePath,
  };
}

/** Write context into a new URLSearchParams (non-GFE keys preserved if provided). */
export function applyContextToSearchParams(
  ctx: UniversalBusinessContext,
  existing?: URLSearchParams | string,
): URLSearchParams {
  const base =
    typeof existing === 'string'
      ? new URLSearchParams(existing.startsWith('?') ? existing.slice(1) : existing)
      : existing
        ? new URLSearchParams(existing)
        : new URLSearchParams();

  for (const k of GFE_URL_KEYS) base.delete(k);

  const set = (k: string, v: string | null | undefined) => {
    if (v != null && v !== '') base.set(k, v);
  };

  set('gfe_seller', ctx.sellerId);
  if (ctx.sellerLabel && ctx.sellerLabel !== '—') set('gfe_seller_label', ctx.sellerLabel);
  set('gfe_conn', ctx.connectionId);
  if (ctx.marketplace) set('gfe_mp', ctx.marketplace);
  if (ctx.dateRange !== DEFAULT_CONTEXT.dateRange) set('gfe_date', ctx.dateRange);
  if (ctx.comparePeriod !== DEFAULT_CONTEXT.comparePeriod) set('gfe_cmp', ctx.comparePeriod);
  set('gfe_bu', ctx.businessUnit);
  set('gfe_wh', ctx.warehouseId);
  set('gfe_state', ctx.state);
  set('gfe_city', ctx.city);
  set('gfe_cat', ctx.category);
  set('gfe_subcat', ctx.subCategory);
  set('gfe_brand', ctx.brand);
  set('gfe_asin', ctx.asin);
  set('gfe_sku', ctx.sku);
  set('gfe_fnsku', ctx.fnsku);
  set('gfe_ptype', ctx.productType);
  set('gfe_ostatus', ctx.orderStatus);
  set('gfe_istatus', ctx.inventoryStatus);
  set('gfe_pstatus', ctx.pricingStatus);
  set('gfe_rstatus', ctx.returnStatus);
  if (ctx.hour != null) set('gfe_hour', String(ctx.hour));
  set('gfe_day', ctx.day);
  set('gfe_week', ctx.week);
  set('gfe_month', ctx.month);
  set('gfe_quarter', ctx.quarter);
  set('gfe_year', ctx.year);
  if (ctx.timeGrain) set('gfe_grain', ctx.timeGrain);
  if (ctx.search) set('gfe_q', ctx.search);
  set('gfe_ws', ctx.lastWorkspacePath);

  return base;
}

/** Stable compact query string for share / equality (no leading ?). */
export function encodeContextQuery(ctx: UniversalBusinessContext): string {
  return applyContextToSearchParams(ctx).toString();
}

export function decodeContextQuery(
  search: string,
  base?: UniversalBusinessContext,
): UniversalBusinessContext {
  const raw = search.startsWith('?') ? search.slice(1) : search;
  return contextFromSearchParams(new URLSearchParams(raw), base ?? DEFAULT_CONTEXT);
}

/** Merge partial patch into context (null / '' clears optional strings). */
export function patchContext(
  prev: UniversalBusinessContext,
  patch: Partial<UniversalBusinessContext>,
): UniversalBusinessContext {
  const next = { ...prev, ...patch };
  if ('sellerLabel' in patch && (patch.sellerLabel === '' || patch.sellerLabel == null)) {
    next.sellerLabel = '—';
  }
  if ('search' in patch && patch.search == null) next.search = '';
  return next;
}
